import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import DashboardVisualizationChart from './DashboardVisualizationChart';
import DashboardWidgetCard from './DashboardWidgetCard';
import { buildDashboardPreviewSampleData } from './dashboardPreviewModel';

const DashboardDraftPreview = ({ draftVersion }) => {
  const [open, setOpen] = useState(false);
  const preview = useMemo(
    () => buildDashboardPreviewSampleData(draftVersion),
    [draftVersion]
  );
  return (
    <Paper
      data-testid="dashboard-draft-preview"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        mt: 4,
        p: { xs: 2, md: 3 }
      }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography component="h2" fontSize={22} fontWeight={700}>
              Preview dashboard
            </Typography>
            <Chip
              label={'Draft version ' + draftVersion.versionNumber}
              size="small"
              variant="outlined"
            />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Check the widgets and graphs before publishing this draft. Preview
            values are simulated and never saved.
          </Typography>
        </Box>
        <Button onClick={() => setOpen(true)} variant="contained">
          Open draft preview
        </Button>
      </Stack>

      <Dialog
        fullWidth
        maxWidth="lg"
        onClose={() => setOpen(false)}
        open={open}
        scroll="paper"
      >
        <DialogTitle>
          <Stack
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <span>{draftVersion.name}</span>
            <Chip color="warning" label="Draft preview" size="small" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            <AlertTitle>Sample preview - not live</AlertTitle>
            All values below are simulated from the configured metric types and
            ranges. Nothing in this preview is saved, approved, or visible to
            dashboard viewers.
          </Alert>

          {draftVersion.description && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {draftVersion.description}
            </Typography>
          )}
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
            Sample period: {preview.fromDate} to {preview.toDate}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography component="h3" fontSize={18} fontWeight={700}>
              Dashboard widgets
            </Typography>
            {preview.widgets.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(auto-fit, minmax(280px, 1fr))'
                  },
                  mt: 1.5
                }}
              >
                {preview.widgets.map((widget) => (
                  <DashboardWidgetCard
                    key={widget.dashboardWidgetId}
                    widget={widget}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                No widgets are configured in this draft.
              </Alert>
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography component="h3" fontSize={18} fontWeight={700}>
              Graphs
            </Typography>
            {preview.visualizations.length > 0 ? (
              <Box sx={{ display: 'grid', gap: 2.5, mt: 1.5 }}>
                {preview.visualizations.map((visualization) => (
                  <DashboardVisualizationChart
                    key={visualization.dashboardVisualizationId}
                    seriesMetrics={preview.seriesMetrics}
                    visualization={visualization}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                No graphs are configured in this draft.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close preview</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DashboardDraftPreview;
